import { ICounters } from '@desp-aas/desp-ui-fwk';
import { useEffect, useRef, useState } from 'react';
import { BsSearch, BsCloudUpload } from 'react-icons/bs';
import { serviceLogo } from '../../theme/images';
import bannerBackground from './banner.webp';
import AssetTypeModal, { ModalMode } from './AssetTypeModal';
import './Hero.css';

type Props = { counts: ICounters | undefined };

function useCountUp(target: number | undefined, duration = 1400) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (target === undefined) return;
        const start = performance.now();
        const tick = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(eased * target));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [target, duration]);
    return target === undefined ? '—' : value;
}

export default function Hero({ counts }: Props) {
    const heroRef = useRef<HTMLDivElement>(null);
    const [modal, setModal] = useState<ModalMode | null>(null);

    useEffect(() => {
        const container = document.getElementById('app');
        if (!container) return;
        const onScroll = () => {
            if (!heroRef.current) return;
            heroRef.current.style.backgroundPositionY = `calc(50% + ${container.scrollTop * 0.35}px)`;
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const root = heroRef.current;
        if (!root) return;
        const revealEls: [string, string, number][] = [
            ['#hero-eyebrow',     'hero-reveal', 0.00],
            ['#hero-title',       'hero-reveal', 0.10],
            ['#hero-tagline',     'hero-reveal', 0.20],
            ['#hero-description', 'hero-reveal', 0.30],
            ['#hero-ctas',        'hero-reveal', 0.40],
            ['#hero-stats',       'hero-reveal', 0.50],
            ['#hero-right',       'hero-reveal-fade', 0.15],
        ];
        revealEls.forEach(([sel, cls, delay]) => {
            const el = root.querySelector<HTMLElement>(sel);
            if (!el) return;
            el.classList.add(cls);
            el.style.transitionDelay = `${delay}s`;
        });
        const observer = new IntersectionObserver(
            entries => entries.forEach(e => {
                const el = e.target as HTMLElement;
                const isFade = el.classList.contains('hero-reveal-fade');
                const fromBelow = e.boundingClientRect.top > 0;
                if (e.isIntersecting) {
                    el.style.setProperty('--sm-transform', fromBelow ? 'translateY(24px)' : 'translateY(-24px)');
                    requestAnimationFrame(() => el.classList.add(isFade ? 'hero-revealed-fade' : 'hero-revealed'));
                } else {
                    el.style.setProperty('--sm-transform', fromBelow ? 'translateY(-24px)' : 'translateY(24px)');
                    el.classList.remove(isFade ? 'hero-revealed-fade' : 'hero-revealed');
                }
            }),
            { threshold: 0.1 }
        );
        revealEls.forEach(([sel]) => {
            const el = root.querySelector<HTMLElement>(sel);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const datasets = useCountUp(counts?.dataset);
    const models = useCountUp(counts?.model);
    const applications = useCountUp(counts?.application);
    const papers = useCountUp(counts?.paper);

    return (
        <>
        <div id="hero" ref={heroRef} style={{ backgroundImage: `url(${bannerBackground})` }}>
            <div id="hero-overlay" />
            <div id="hero-content">
                <div id="hero-left">
                    <div id="hero-eyebrow">DestinE Platform</div>
                    <h1 id="hero-title">
                        <span className="gradient-text">Collaborative</span> Services
                    </h1>
                    <p id="hero-tagline">Discover · Share · Improve</p>
                    <p id="hero-description">
                        The DestinE platform for discovering, sharing and improving scientific assets related to Earth Observation, climate and environmental applications.<br />
                        Find data, application, model and code.<br />
                        Publish your own assets.<br />
                        Contribute to existing ones and help the community build better solutions together.
                    </p>
                    <div id="hero-ctas">
                        <button className="btn-primary" onClick={() => setModal('explore')}>
                            <BsSearch /> Explore assets
                        </button>
                        <button className="btn-outline" onClick={() => setModal('publish')}>
                            <BsCloudUpload /> Publish an asset
                        </button>
                    </div>

                    <div id="hero-stats">
                        <div className="hero-stat-item">
                            <span className="hero-stat-value">{datasets}</span>
                            <span className="hero-stat-label">Datasets</span>
                        </div>
                        <div className="hero-stat-sep" />
                        <div className="hero-stat-item">
                            <span className="hero-stat-value">{models}</span>
                            <span className="hero-stat-label">Models</span>
                        </div>
                        <div className="hero-stat-sep" />
                        <div className="hero-stat-item">
                            <span className="hero-stat-value">{applications}</span>
                            <span className="hero-stat-label">Applications</span>
                        </div>
                        <div className="hero-stat-sep" />
                        <div className="hero-stat-item">
                            <span className="hero-stat-value">{papers}</span>
                            <span className="hero-stat-label">Documents</span>
                        </div>
                    </div>
                </div>

                <div id="hero-right">
                    <div id="hero-ring-outer" />
                    <div id="hero-ring-inner" />
                    <img src={serviceLogo} id="hero-logo" alt="Collaborative Services" />
                </div>
            </div>
        </div>

        <AssetTypeModal mode={modal} onClose={() => setModal(null)} />
        </>
    );
}
